<template>
  <div class="set-password-shell">
    <RegistrationHeader :breadcrumb="$t('registration.set_password.breadcrumb')" :show-sign-in="false" />

    <main class="set-password-shell__pane">
      <section
        class="tw:mx-auto tw:mt-10 tw:flex tw:w-full tw:max-w-[420px] tw:flex-col tw:gap-7"
        data-testid="reg-set-password">
        <RegistrationStepHeading
          :title="$t('registration.set_password.title')"
          :lede="$t('registration.set_password.lede')" />

        <form novalidate class="tw:flex tw:flex-col tw:gap-7" @submit.prevent="submit">
          <div class="tw:flex tw:flex-col tw:gap-[18px]">
            <LeadInput
              :model-value="email"
              type="email"
              :label="$t('registration.account.email')"
              disabled
              data-testid="reg-set-password-email">
              <template #iconStart><IconMail :size="16" /></template>
            </LeadInput>

            <div class="tw:flex tw:flex-col tw:gap-2">
              <LeadInput
                v-model="password"
                type="password"
                autocomplete="new-password"
                :label="$t('registration.set_password.new_password')"
                :errors="fieldErrors.password"
                required
                data-testid="reg-set-password-new">
                <template #iconStart><IconLock :size="16" /></template>
              </LeadInput>

              <PasswordStrengthMeter :password="password" />
            </div>

            <LeadInput
              v-model="confirmPassword"
              type="password"
              autocomplete="new-password"
              :label="$t('registration.set_password.confirm_password')"
              :errors="fieldErrors.confirmPassword"
              required
              data-testid="reg-set-password-confirm">
              <template #iconStart><IconLock :size="16" /></template>
            </LeadInput>
          </div>

          <LeadButton
            type="submit"
            variant="primary"
            class="tw:w-full"
            :loading="isSubmitting"
            data-testid="reg-set-password-submit">
            {{ $t('registration.set_password.submit') }}
          </LeadButton>
        </form>
      </section>
    </main>
  </div>
</template>

<script setup lang="ts">
/**
 * Lets a signed-in registrant with an unfinished wizard set a new password. Reached only from the reset flow,
 * which already proved address ownership, so like the backend endpoint it asks for the new password only.
 * The password is never written to the store or session storage.
 */
import { computed, reactive, ref } from 'vue';
import { useRouter } from 'vue-router';
import { IconLock, IconMail } from '@tabler/icons-vue';

import { registrationClient } from '@/api/registration';
import LeadButton from '@/components/lead-design/LeadButton.vue';
import LeadInput from '@/components/lead-design/LeadInput.vue';
import PasswordStrengthMeter from '@/components/registration/subcomponents/PasswordStrengthMeter.vue';
import RegistrationHeader from '@/components/registration/subcomponents/RegistrationHeader.vue';
import RegistrationStepHeading from '@/components/registration/subcomponents/RegistrationStepHeading.vue';
import { isPasswordAcceptable } from '@/components/registration/subcomponents/passwordStrength';
import translator from '@/locale/translator';
import { useUserStore } from '@/stores/user';

/* ----------------------------------- STATE ---------------------------------- */
const userStore = useUserStore();
const router = useRouter();

const password = ref('');
const confirmPassword = ref('');
const isSubmitting = ref(false);
const fieldErrors = reactive<{ password: string; confirmPassword: string }>({ password: '', confirmPassword: '' });

/* --------------------------------- COMPUTED --------------------------------- */
const email = computed(() => userStore.currentUser?.email ?? '');

/* --------------------------------- METHODS ---------------------------------- */
function validate() {
  fieldErrors.password = '';
  fieldErrors.confirmPassword = '';

  if (!isPasswordAcceptable(password.value)) {
    fieldErrors.password = translator.get('registration.account.password_invalid');
  }
  if (password.value !== confirmPassword.value) {
    fieldErrors.confirmPassword = translator.get('registration.set_password.mismatch');
  }

  return !fieldErrors.password && !fieldErrors.confirmPassword;
}

async function submit() {
  if (!validate()) return;

  isSubmitting.value = true;
  try {
    await registrationClient.setPassword(password.value);
    // the router guard reads registration_step from this store; a stale value would send them to the dashboard
    await userStore.fetchCurrent();
    router.push(userStore.authenticatedLandingRoute());
  } catch {
    // the shared client already showed a toast
  } finally {
    isSubmitting.value = false;
  }
}
</script>

<style scoped lang="scss">
.set-password-shell {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: #fff;

  &__pane {
    flex: 1;
    padding: 40px 24px;
  }
}
</style>
